/**
 * Which carrier a pasted tracking number belongs to, and where to follow it.
 *
 * Mirrors nestjs urn-tracking/carrier.util.ts, which builds the link the family
 * is texted — change both together.
 */
export type Carrier =
  | 'ups'
  | 'canada_post'
  | 'fedex'
  | 'purolator'
  | 'usps'
  | 'dhl'
  | 'other';

export const CARRIER_LABELS: Record<Carrier, string> = {
  ups: 'UPS',
  canada_post: 'Canada Post',
  fedex: 'FedEx',
  purolator: 'Purolator',
  usps: 'USPS',
  dhl: 'DHL',
  other: 'Other',
};

export const CARRIERS = Object.keys(CARRIER_LABELS) as Carrier[];

/** '1z v56d 2620 0896 5281' → '1ZV56D262008965281'. */
export function normalizeTrackingNumber(raw?: string | null): string {
  return (raw ?? '').replace(/[\s-]+/g, '').toUpperCase();
}

export interface CarrierGuess {
  carrier: Carrier | null;
  /** Another carrier shares the format — the carrier shown is a default. */
  ambiguousWith?: Carrier;
}

/**
 * Best guess from the number's shape. Only UPS ('1Z' + 16) and the UPU S10
 * format name their carrier outright; a 12-digit number is FedEx's usual format
 * but also Purolator's, so it defaults to FedEx and says so.
 */
export function detectCarrier(raw?: string | null): CarrierGuess {
  const n = normalizeTrackingNumber(raw);
  if (!n) return { carrier: null };

  if (/^1Z[0-9A-Z]{16}$/.test(n)) return { carrier: 'ups' };

  const s10 = /^[A-Z]{2}\d{9}([A-Z]{2})$/.exec(n);
  if (s10) {
    if (s10[1] === 'CA') return { carrier: 'canada_post' };
    if (s10[1] === 'US') return { carrier: 'usps' };
    return { carrier: null };
  }

  if (/^\d{16}$/.test(n)) return { carrier: 'canada_post' };
  if (/^9[2-5]\d{18,20}$/.test(n)) return { carrier: 'usps' };
  if (/^96\d{20}$/.test(n)) return { carrier: 'fedex' };
  if (/^\d{12}$/.test(n)) return { carrier: 'fedex', ambiguousWith: 'purolator' };
  if (/^(\d{15}|\d{20})$/.test(n)) return { carrier: 'fedex' };
  if (/^[A-Z]{3}\d{9}$/.test(n)) return { carrier: 'purolator' };
  if (/^\d{10}$/.test(n)) return { carrier: 'dhl' };

  return { carrier: null };
}

export function isCarrier(value: unknown): value is Carrier {
  return typeof value === 'string' && value in CARRIER_LABELS;
}

/** The carrier's public tracking page (English — this link is for staff). */
export function carrierTrackingUrl(
  carrier: Carrier | null | undefined,
  raw: string,
): string | null {
  const plain = normalizeTrackingNumber(raw);
  const n = encodeURIComponent(plain);
  if (!n) return null;
  switch (carrier) {
    case 'ups':
      return `https://www.ups.com/track?tracknum=${n}`;
    case 'canada_post':
      return `https://www.canadapost-postescanada.ca/track-reperage/en/details/${n}`;
    case 'fedex':
      return `https://www.fedex.com/fedextrack/?trknbr=${n}`;
    case 'purolator':
      return `https://www.purolator.com/en/shipping/tracker?pin=${n}`;
    case 'usps':
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`;
    case 'dhl':
      return `https://www.dhl.com/ca-en/home/tracking/tracking-express.html?submit=1&tracking-id=${n}`;
    default:
      return null;
  }
}

/**
 * PostgREST / Postgres "column doesn't exist", as the data provider raises it
 * (an HttpError whose body is PostgREST's JSON). The admin can go live before
 * 20260925230000_urn_tracking_notify adds urn_tracking_carrier — CI migrations
 * and the Railway deploy run side by side — so the dialog saves without it.
 */
export function isMissingColumnError(error: unknown): boolean {
  const e = error as {
    code?: string;
    message?: string;
    body?: { code?: string; message?: string };
  } | null;
  const code = e?.body?.code ?? e?.code ?? '';
  const message = `${e?.body?.message ?? ''} ${e?.message ?? ''}`;
  return (
    ['42703', 'PGRST204'].includes(code) ||
    /could not find the .* column|column .* does not exist/i.test(message)
  );
}
